import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Table,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getPayement } from "helpers/requests";
import logo from "./logo.png";

// ── Helpers ────────────────────────────────────────────────────────────
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR") : "—";
const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
const formatMoney = (n) =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString("fr-DZ") + " DA" : "—";

const TYPE_META = {
  HOTEL_RESERVATION: {
    label: "Réservation hôtel",
    color: "primary",
    icon: "mdi-bed",
  },
  RESERVATION: {
    label: "Voyage organisé",
    color: "info",
    icon: "mdi-airplane",
  },
  VISA: {
    label: "Visa",
    color: "warning",
    icon: "mdi-passport",
  },
};

// Reusable info row
const InfoRow = ({ label, value, full = false }) =>
  value || value === 0 ? (
    <Col md={full ? 12 : 6} className="mb-3">
      <p className="text-muted fw-medium mb-1 font-size-12 text-uppercase">
        {label}
      </p>
      <p className="mb-0">{value}</p>
    </Col>
  ) : null;

const PaymentDetail = ({ t }) => {
  const { id } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentDetail = async () => {
      try {
        const response = await getPayement(id);
        setPayment(response);
      } catch (error) {
        console.error("Error fetching payment details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPaymentDetail();
  }, [id]);

  // ── PDF generator — classic invoice/receipt layout ────────────────
  const generatePDF = async () => {
    if (!payment) {
      alert("Le paiement n'est pas encore charge.");
      return;
    }

    try {
      // Sanitize: pdf-lib standard Helvetica only supports WinAnsi.
      // Strip diacritics so drawText never throws silently.
      const safe = (v) => {
        if (v == null) return "-";
        return String(v)
          .normalize("NFKD")
          .replace(/[̀-ͯ]/g, "")
          .replace(/[^\x00-\x7F]/g, "");
      };

      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([595.28, 841.89]);
      const { width, height } = page.getSize();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // Palette
      const navy   = rgb(0.0157, 0.196, 0.388);
      const accent = rgb(0.137, 0.62, 0.996);
      const text   = rgb(0.13, 0.14, 0.18);
      const muted  = rgb(0.45, 0.48, 0.54);
      const rule   = rgb(0.85, 0.87, 0.91);
      const zebra  = rgb(0.97, 0.98, 0.99);

      const margin = 50;
      const innerW = width - margin * 2;
      const right  = width - margin;
      const white  = rgb(1, 1, 1);

      // Helper: text width
      const w = (s, size, f = font) => f.widthOfTextAtSize(safe(s), size);

      // Helper: draw text right-aligned at x
      const drawRight = (s, x, y, size, f, color = text) => {
        page.drawText(safe(s), { x: x - w(s, size, f), y, size, font: f, color });
      };

      // ─────────────────────────────────────────────────────────
      // 1) HEADER — logo on the left, big "REÇU" + meta on the right
      // ─────────────────────────────────────────────────────────
      let y = height - margin;

      // Logo
      let logoH = 0;
      try {
        const logoBytes = await fetch(logo).then((r) => r.arrayBuffer());
        const logoImg = await pdfDoc.embedPng(logoBytes);
        logoH = 42;
        const logoW = logoH * (logoImg.width / logoImg.height);
        page.drawImage(logoImg, {
          x: margin,
          y: y - logoH,
          width: logoW,
          height: logoH,
        });
      } catch {}

      // Brand block under the logo
      page.drawText(safe("ProTour"), {
        x: margin,
        y: y - logoH - 14,
        size: 11,
        font: bold,
        color: navy,
      });
      page.drawText(safe("Agence de voyage"), {
        x: margin,
        y: y - logoH - 26,
        size: 8.5,
        font,
        color: muted,
      });

      // Right side: huge "RECU" word + receipt meta
      drawRight("RECU", right, y - 12, 32, bold, navy);
      const numLabel = "N";
      const numVal   = `${payment.id.slice(0, 8).toUpperCase()}`;
      drawRight(numLabel, right - w(numVal, 9, bold) - 8, y - 32, 8.5, font, muted);
      drawRight(numVal, right, y - 32, 9, bold, text);

      const dateLabel = "Date";
      const dateVal   = formatDate(payment.createdAt);
      drawRight(dateLabel, right - w(dateVal, 9, bold) - 8, y - 46, 8.5, font, muted);
      drawRight(dateVal, right, y - 46, 9, bold, text);

      const statLabel = "Statut";
      const statVal   = "PAYE";
      drawRight(statLabel, right - w(statVal, 9, bold) - 8, y - 60, 8.5, font, muted);
      drawRight(statVal, right, y - 60, 9, bold, rgb(0.086, 0.639, 0.290));

      // Top accent rule
      y = y - logoH - 50;
      page.drawLine({
        start: { x: margin, y },
        end:   { x: right, y },
        thickness: 1.2,
        color: navy,
      });

      // ─────────────────────────────────────────────────────────
      // 2) BILL-TO + FROM (two columns under the rule)
      // ─────────────────────────────────────────────────────────
      y -= 22;
      const colGap = 20;
      const halfW  = (innerW - colGap) / 2;

      // FROM (left)
      page.drawText(safe("DE"), { x: margin, y, size: 8, font: bold, color: muted });
      // BILL TO (right)
      page.drawText(safe("PAYE PAR"), { x: margin + halfW + colGap, y, size: 8, font: bold, color: muted });
      y -= 14;

      const drawAddressLine = (s, x, ly, opts = {}) =>
        page.drawText(safe(s || "-"), {
          x, y: ly,
          size: opts.size || 9.5,
          font: opts.bold ? bold : font,
          color: opts.color || text,
        });

      drawAddressLine("ProTour", margin, y, { bold: true, size: 11, color: navy });
      drawAddressLine(`${payment.firstname} ${payment.lastname}`, margin + halfW + colGap, y, { bold: true, size: 11, color: navy });
      y -= 14;
      drawAddressLine("Agence de voyage", margin, y, { color: muted });
      drawAddressLine(payment.email || "-", margin + halfW + colGap, y, { color: muted });
      y -= 13;
      drawAddressLine("contact@protour.dz", margin, y, { color: muted });
      drawAddressLine(payment.phonenumber || "-", margin + halfW + colGap, y, { color: muted });

      // ─────────────────────────────────────────────────────────
      // 3) ITEMS TABLE — Description / Qty / Prix unit. / Montant
      // ─────────────────────────────────────────────────────────
      y -= 38;
      const colDesc = margin;
      const colQty  = right - 230;
      const colUnit = right - 130;
      const colAmt  = right;
      const tblHead = (s, x, opts = {}) => {
        const size = 8;
        if (opts.right) drawRight(s, x, y, size, bold, white);
        else page.drawText(safe(s), { x, y, size, font: bold, color: white });
      };

      // Header row (filled bar)
      page.drawRectangle({
        x: margin, y: y - 6,
        width: innerW, height: 22,
        color: navy,
      });
      const headBaseline = y + 1;
      const oldY = y;
      y = headBaseline;
      page.drawText(safe("DESCRIPTION"), { x: colDesc + 10, y, size: 8, font: bold, color: white });
      drawRight("QTE", colQty + 30, y, 8, bold, white);
      drawRight("PRIX UNIT.", colUnit + 30, y, 8, bold, white);
      drawRight("MONTANT", colAmt - 10, y, 8, bold, white);
      y = oldY - 6 - 14; // below the header bar

      // Item rows
      const meta = TYPE_META[payment.type] || { label: payment.type };
      const hr = payment.hotelReservation;
      const r  = payment.reservation;
      const v  = payment.visa;

      // Build a description for the main item + 1-3 sub-detail lines
      let itemTitle = meta.label;
      let itemSubs  = [];
      if (hr) {
        itemTitle = `Reservation hotel - ${hr.property?.name || "-"}`;
        itemSubs = [
          `Reservant: ${hr.name || ""} ${hr.surnom || ""}`.trim(),
          `Arrivee ${formatDate(hr.checkinDate)} -> Depart ${formatDate(hr.checkoutDate)} (${hr.totalNights || 0} nuit(s))`,
          `Occupants: ${hr.numAdults || 0} adulte(s), ${hr.numChildren || 0} enfant(s)`,
        ];
      } else if (r) {
        itemTitle = `Voyage organise - ${r.voyage?.title || "-"}`;
        itemSubs = [
          `Destination: ${r.voyage?.cite || "-"} - Compagnie: ${r.voyage?.airline || "-"}`,
          `Depart ${formatDate(r.checkin_date)} -> Retour ${formatDate(r.checkout_date)}`,
          `Voyageurs: ${r.n_adult || 0} ad, ${r.n_enfant || 0} enf, ${r.n_bebe || 0} beb - ${r.nationality || "-"}`,
        ];
      } else if (v) {
        itemTitle = `Demande de visa - ${v.country || "-"} (${v.visaType || ""})`;
        itemSubs = [
          `Demandeur: ${v.applicantName || ""} ${v.applicantSurname || ""}`.trim(),
          `Nationalite: ${v.nationality || "-"} - Date voyage: ${formatDate(v.startDate)}`,
          `Passeport: ${v.passportNumber || "-"} (exp. ${formatDate(v.expirationDat)}) - ${v.numPersons || 1} pers.`,
        ];
      } else {
        itemSubs = [`ID reference: ${payment.id}`];
      }

      const rowTopPadding = 6;
      const rowSubLineH = 12;
      const rowH = rowTopPadding + 14 + itemSubs.length * rowSubLineH + 8;

      // Zebra background for the row
      page.drawRectangle({
        x: margin, y: y - rowH + 14,
        width: innerW, height: rowH,
        color: zebra,
      });

      // Item title
      page.drawText(safe(itemTitle).slice(0, 70), {
        x: colDesc + 10,
        y,
        size: 10.5,
        font: bold,
        color: text,
      });
      // Qty / Unit / Amount on the title line
      drawRight("1", colQty + 30, y, 10, font, text);
      drawRight(formatMoney(payment.amount), colUnit + 30, y, 10, font, text);
      drawRight(formatMoney(payment.amount), colAmt - 10, y, 10.5, bold, text);

      // Sub-detail lines (still inside the row, indented)
      itemSubs.forEach((line, i) => {
        page.drawText(safe(line).slice(0, 90), {
          x: colDesc + 10,
          y: y - 14 - i * rowSubLineH,
          size: 8.5,
          font,
          color: muted,
        });
      });

      y -= rowH - 4;

      // Bottom rule of the table
      page.drawLine({
        start: { x: margin, y: y + 4 },
        end:   { x: right,  y: y + 4 },
        thickness: 0.5,
        color: rule,
      });

      // ─────────────────────────────────────────────────────────
      // 4) TOTALS — right-aligned stack
      // ─────────────────────────────────────────────────────────
      y -= 14;
      const totalsX = right - 220;

      const drawTotalRow = (label, value, opts = {}) => {
        page.drawText(safe(label), {
          x: totalsX,
          y,
          size: opts.bold ? 11 : 9.5,
          font: opts.bold ? bold : font,
          color: opts.bold ? navy : muted,
        });
        drawRight(
          value,
          right,
          y,
          opts.bold ? 14 : 10,
          opts.bold ? bold : (opts.valueBold ? bold : font),
          opts.bold ? navy : text
        );
        y -= opts.bold ? 0 : 16;
      };

      drawTotalRow("Sous-total", formatMoney(payment.amount), { valueBold: true });
      drawTotalRow("Frais", "0 DA");
      drawTotalRow("Taxe", "0 DA");

      // Divider before total
      y -= 4;
      page.drawLine({
        start: { x: totalsX, y },
        end:   { x: right,  y },
        thickness: 0.5,
        color: rule,
      });
      y -= 18;

      // Final total row (highlighted)
      page.drawRectangle({
        x: totalsX - 8,
        y: y - 8,
        width: right - totalsX + 8,
        height: 28,
        color: navy,
      });
      page.drawText(safe("TOTAL"), {
        x: totalsX,
        y,
        size: 11,
        font: bold,
        color: white,
      });
      drawRight(formatMoney(payment.amount), right - 8, y, 14, bold, white);
      y -= 30;

      // ─────────────────────────────────────────────────────────
      // 5) NOTES (optional)
      // ─────────────────────────────────────────────────────────
      if (payment.notes) {
        y -= 8;
        page.drawText(safe("NOTES"), { x: margin, y, size: 8, font: bold, color: muted });
        y -= 12;
        // Wrap into 2 lines max, ~95 chars per line
        const note = safe(payment.notes);
        const lines = [];
        let rest = note;
        while (rest.length > 0 && lines.length < 2) {
          lines.push(rest.slice(0, 95));
          rest = rest.slice(95);
        }
        lines.forEach((ln, i) => {
          page.drawText(ln, {
            x: margin,
            y: y - i * 12,
            size: 9,
            font,
            color: text,
          });
        });
      }

      // ─────────────────────────────────────────────────────────
      // 6) FOOTER — thank you banner anchored at the bottom
      // ─────────────────────────────────────────────────────────
      const footerY = 70;
      page.drawLine({
        start: { x: margin, y: footerY + 22 },
        end:   { x: right,  y: footerY + 22 },
        thickness: 0.5,
        color: rule,
      });
      // Thank you (bold accent on left)
      page.drawText(safe("Merci pour votre paiement."), {
        x: margin,
        y: footerY,
        size: 10.5,
        font: bold,
        color: accent,
      });
      page.drawText(safe("ProTour - Votre agence de confiance"), {
        x: margin,
        y: footerY - 14,
        size: 8.5,
        font,
        color: muted,
      });
      // Generation timestamp on the right
      drawRight(`Genere le ${formatDateTime(new Date())}`, right, footerY - 14, 8, font, muted);
      drawRight(`Page 1 / 1`, right, footerY, 8, font, muted);

      // ─────────────────────────────────────────────────────────
      // Download
      // ─────────────────────────────────────────────────────────
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `recu_${payment.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Echec de la generation du PDF. Verifiez la console pour le detail.");
    }
  };

  // ── Loading state ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={t("Paiements")} breadcrumbItem={t("Détails")} />
          <Card>
            <CardBody className="text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Chargement…</span>
              </div>
              <p className="text-muted mb-0">Chargement du paiement…</p>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={t("Paiements")} breadcrumbItem={t("Détails")} />
          <Card>
            <CardBody className="text-center py-5">
              <i className="mdi mdi-alert-circle-outline display-4 text-muted" />
              <h5 className="mt-3">Paiement introuvable</h5>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  const meta = TYPE_META[payment.type] || {
    label: payment.type,
    color: "secondary",
    icon: "mdi-cash",
  };
  const hr = payment.hotelReservation;
  const r = payment.reservation;
  const v = payment.visa;

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title={t("Paiements")} breadcrumbItem={t("Détails")} />

        {/* ── Hero summary ─────────────────────────────────────── */}
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Row className="align-items-center">
                  <Col md={8}>
                    <div className="d-flex align-items-center">
                      <div
                        className={`avatar-md me-3`}
                      >
                        <span
                          className={`avatar-title rounded-circle bg-${meta.color}-subtle text-${meta.color} font-size-24`}
                        >
                          <i className={`mdi ${meta.icon}`} />
                        </span>
                      </div>
                      <div>
                        <h5 className="mb-1">
                          {payment.firstname} {payment.lastname}
                        </h5>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <Badge color={meta.color} className="font-size-11">
                            {meta.label}
                          </Badge>
                          <span className="text-muted font-size-12">
                            #{payment.id.slice(0, 8).toUpperCase()}
                          </span>
                        </div>
                        <small className="text-muted">
                          <i className="mdi mdi-clock-outline me-1" />
                          {formatDateTime(payment.createdAt)}
                        </small>
                      </div>
                    </div>
                  </Col>
                  <Col md={4} className="text-md-end mt-3 mt-md-0">
                    <p className="text-muted mb-1 font-size-12 text-uppercase">
                      Montant
                    </p>
                    <h3 className="text-primary mb-0">
                      {formatMoney(payment.amount)}
                    </h3>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* ── Payer info ──────────────────────────────────── */}
          <Col lg={6}>
            <Card>
              <CardBody>
                <CardTitle className="mb-3">
                  <i className="mdi mdi-account-outline text-primary me-2" />
                  Informations du payeur
                </CardTitle>
                <Row>
                  <InfoRow label="Prénom" value={payment.firstname} />
                  <InfoRow label="Nom" value={payment.lastname} />
                  <InfoRow label="Téléphone" value={payment.phonenumber} />
                  <InfoRow label="E-mail" value={payment.email || "—"} />
                  <InfoRow label="ID paiement" value={payment.id} full />
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* ── Linked source ───────────────────────────────── */}
          <Col lg={6}>
            <Card>
              <CardBody>
                <CardTitle className="mb-3">
                  <i className={`mdi ${meta.icon} text-${meta.color} me-2`} />
                  {hr
                    ? "Réservation hôtel"
                    : r
                    ? "Voyage organisé"
                    : v
                    ? "Demande de visa"
                    : "Source"}
                </CardTitle>

                {!hr && !r && !v && (
                  <div className="text-muted text-center py-4">
                    <i className="mdi mdi-link-off display-6 d-block mb-2" />
                    Ce paiement n'est lié à aucune réservation ou demande de
                    visa enregistrée.
                  </div>
                )}

                {hr && (
                  <Row>
                    <InfoRow label="Hôtel" value={hr.property?.name} />
                    <InfoRow
                      label="Statut"
                      value={
                        <Badge
                          color={
                            hr.status === "ACCEPTED" ||
                            hr.status === "HOTEL_ACCEPTED"
                              ? "success"
                              : hr.status === "PENDING"
                              ? "warning"
                              : "danger"
                          }
                        >
                          {hr.status}
                        </Badge>
                      }
                    />
                    <InfoRow label="Arrivée" value={formatDate(hr.checkinDate)} />
                    <InfoRow label="Départ" value={formatDate(hr.checkoutDate)} />
                    <InfoRow label="Nuits" value={hr.totalNights} />
                    <InfoRow
                      label="Occupants"
                      value={`${hr.numAdults || 0} adulte(s), ${hr.numChildren || 0} enfant(s)`}
                    />
                    <InfoRow
                      label="Total réservation"
                      value={formatMoney(hr.totalPrice)}
                    />
                    <InfoRow label="Téléphone client" value={hr.phone} />
                    <InfoRow label="Adresse" value={hr.address} full />
                  </Row>
                )}

                {r && (
                  <Row>
                    <InfoRow label="Voyage" value={r.voyage?.title} />
                    <InfoRow
                      label="Statut"
                      value={
                        <Badge
                          color={
                            r.status === "CONFIRMED"
                              ? "success"
                              : r.status === "PENDING"
                              ? "warning"
                              : "danger"
                          }
                        >
                          {r.status}
                        </Badge>
                      }
                    />
                    <InfoRow label="Destination" value={r.voyage?.cite} />
                    <InfoRow label="Compagnie" value={r.voyage?.airline} />
                    <InfoRow label="Départ" value={formatDate(r.checkin_date)} />
                    <InfoRow label="Retour" value={formatDate(r.checkout_date)} />
                    <InfoRow
                      label="Voyageurs"
                      value={`${r.n_adult || 0} adulte(s), ${r.n_enfant || 0} enfant(s), ${r.n_bebe || 0} bébé(s)`}
                    />
                    <InfoRow label="Nationalité" value={r.nationality} />
                    <InfoRow
                      label="Total voyage"
                      value={formatMoney(r.totalAmount)}
                    />
                    <InfoRow label="N° passeport" value={r.n_passport} />
                  </Row>
                )}

                {v && (
                  <Row>
                    <InfoRow label="Pays" value={v.country} />
                    <InfoRow label="Type" value={v.visaType} />
                    <InfoRow
                      label="Statut"
                      value={
                        <Badge
                          color={
                            v.status === "ACCEPTED"
                              ? "success"
                              : v.status === "PENDING"
                              ? "warning"
                              : "danger"
                          }
                        >
                          {v.status}
                        </Badge>
                      }
                    />
                    <InfoRow label="Nationalité" value={v.nationality} />
                    <InfoRow
                      label="Demandeur"
                      value={`${v.applicantName || ""} ${v.applicantSurname || ""}`.trim()}
                    />
                    <InfoRow label="E-mail" value={v.emailAddress} />
                    <InfoRow label="Date voyage" value={formatDate(v.startDate)} />
                    <InfoRow label="Nb personnes" value={v.numPersons} />
                    <InfoRow label="N° passeport" value={v.passportNumber} />
                    <InfoRow
                      label="Expiration passeport"
                      value={formatDate(v.expirationDat)}
                    />
                    <InfoRow
                      label="Total visa"
                      value={formatMoney(v.totalPrice)}
                    />
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Notes ─────────────────────────────────────────── */}
        {payment.notes && (
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-3">
                    <i className="mdi mdi-note-text-outline text-primary me-2" />
                    Notes
                  </CardTitle>
                  <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {payment.notes}
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* ── Total + actions ───────────────────────────────── */}
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Row className="align-items-center">
                  <Col md={6}>
                    <Table borderless className="mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted">Sous-total</td>
                          <td className="text-end">
                            {formatMoney(payment.amount)}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Frais</td>
                          <td className="text-end">0 DA</td>
                        </tr>
                        <tr>
                          <td className="fw-bold">Total payé</td>
                          <td className="text-end fw-bold text-primary font-size-18">
                            {formatMoney(payment.amount)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={6} className="text-md-end mt-3 mt-md-0">
                    <Button color="primary" onClick={generatePDF}>
                      <i className="mdi mdi-file-download-outline me-1" />
                      Télécharger le reçu PDF
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default withTranslation()(PaymentDetail);
